import subscribeToSubjectEvents from '@Application/Subjects/subscribeToSubjectEvents';
import SubscribeToSubjectEvents from '@Application/Subjects/Queries/SubscribeToSubjectEvents';
import leaveSubject from '@Application/Subjects/leaveSubject';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useEventSubscription from '@UI/Hooks/useEventSubscription';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { useDispatch } from '@verclam/synapse';
import * as React from 'react';
import { Outlet } from 'react-router-dom';

/**
 * Holds the dossier's event stream open for the whole visit, so the lists and sheets the sub-screens
 * read follow what the server changes. On leaving the dossier it closes the stream and empties the
 * dossier, so the next entry reads it again; it loads and shows nothing itself. In development,
 * StrictMode's double mount costs at most one extra read.
 */
const SubjectLayout: React.FC = () => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { subject } = useSubjectAddressParams();
	const typeKey = subject?.typeKey ?? null;
	const subjectId = subject?.subjectId ?? null;

	useEventSubscription(
		() => subscribeToSubjectEvents(new SubscribeToSubjectEvents(facilityId, subject as SubjectAddress)),
		subject !== null,
		[facilityId, typeKey, subjectId],
	);

	React.useEffect(() => {
		if (typeKey === null || subjectId === null) {
			return undefined;
		}

		return () => {
			dispatch(leaveSubject(new SubjectAddress(typeKey, subjectId)));
		};
	}, [dispatch, typeKey, subjectId]);

	return <Outlet />;
};

export default SubjectLayout;
